/**
 * Overflow ("more actions") glyph used by the message-feedback row.
 * Renders the design-system's `message-dots-square` mark: a chat
 * bubble shape containing three horizontal dots. Uses `currentColor`
 * so the enclosing `IconButton` controls tinting.
 *
 * The path data is transcribed directly from the GA-Sidekick Figma
 * source (asset hash `a8fe398d12daa3c8cf063bb3e62eb0d950ac34ac`). The
 * Figma artwork's native viewBox is `14.0501 x 14.3756`; it is
 * centered inside a 24x24 viewBox via a `translate(4.975, 4.8122)`
 * group so the icon matches the visual scale of the package's other
 * 24x24 glyphs. The Figma source specifies a slightly thinner 1.3
 * stroke for this glyph (vs. 1.5 for the thumbs icons) so the small
 * dots and bubble outline remain visually balanced.
 */
export type OverflowDotsIconProps = {
  /** Rendered width/height in px. Defaults to 24. */
  size?: number;
  className?: string;
};

function OverflowDotsIcon({ size = 24, className }: OverflowDotsIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <g transform="translate(4.975 4.8122)">
        <path
          d="M3.83756 5.9625H3.84464M7.02506 5.9625H7.03214M10.2126 5.9625H10.2196M3.48339 11.275V12.9293C3.48339 13.3067 3.48339 13.4955 3.56076 13.5924C3.62805 13.6767 3.73008 13.7257 3.83794 13.7256C3.96196 13.7255 4.10932 13.6076 4.40405 13.3718L6.09375 12.02C6.43892 11.7439 6.61151 11.6058 6.80369 11.5077C6.9742 11.4205 7.15569 11.3569 7.34325 11.3184C7.55465 11.275 7.77567 11.275 8.21771 11.275H10.0001C11.1902 11.275 11.7852 11.275 12.2398 11.0434C12.6396 10.8397 12.9647 10.5146 13.1684 10.1147C13.4001 9.66017 13.4001 9.06511 13.4001 7.875V4.05C13.4001 2.85989 13.4001 2.26483 13.1684 1.81027C12.9647 1.41043 12.6396 1.08534 12.2398 0.881612C11.7852 0.65 11.1902 0.65 10.0001 0.65H4.05006C2.85994 0.65 2.26489 0.65 1.81033 0.881612C1.41048 1.08534 1.0854 1.41043 0.881667 1.81027C0.650056 2.26483 0.650056 2.85989 0.650056 4.05V8.44167C0.650056 9.1004 0.650056 9.42976 0.722464 9.69999C0.918956 10.4333 1.49175 11.0061 2.22507 11.2026C2.49529 11.275 2.82466 11.275 3.48339 11.275ZM4.19172 5.9625C4.19172 6.1581 4.03316 6.31667 3.83756 6.31667C3.64196 6.31667 3.48339 6.1581 3.48339 5.9625C3.48339 5.7669 3.64196 5.60833 3.83756 5.60833C4.03316 5.60833 4.19172 5.7669 4.19172 5.9625ZM7.37922 5.9625C7.37922 6.1581 7.22066 6.31667 7.02506 6.31667C6.82946 6.31667 6.67089 6.1581 6.67089 5.9625C6.67089 5.7669 6.82946 5.60833 7.02506 5.60833C7.22066 5.60833 7.37922 5.7669 7.37922 5.9625ZM10.5667 5.9625C10.5667 6.1581 10.4082 6.31667 10.2126 6.31667C10.017 6.31667 9.85839 6.1581 9.85839 5.9625C9.85839 5.7669 10.017 5.60833 10.2126 5.60833C10.4082 5.60833 10.5667 5.7669 10.5667 5.9625Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

export default OverflowDotsIcon;
